import { z } from 'zod'
import { roleSchema } from './rbac.ts'

/**
 * The audit ledger. Append-only and hash-chained, so a silent edit is detectable
 * (docs/01-product-design.md §6).
 *
 * This is what reconciles "the admin group speaks with one voice in chat" with
 * "mutations are tracked per user": every row names the real actor even when the
 * counterparty only ever saw the group alias.
 */

export const AUDIT_ACTIONS = [
  'auth.login',
  'auth.login_failed',
  'auth.logout',
  'auth.password_changed',
  'auth.session_revoked',

  'org.created',
  'org.updated',
  'org.status_changed',

  'user.created',
  'user.updated',
  'user.status_changed',
  'user.password_reset',

  'category.created',
  'category.updated',
  'category.deleted',

  'product.created',
  'product.updated',
  'product.status_changed',
  'product.merged',
  'product_proposal.created',
  'product_proposal.approved',
  'product_proposal.rejected',
  'product_proposal.merged',

  'listing.created',
  'listing.updated',
  'listing.status_changed',
  'listing.tiers_changed',
  'listing.imported',

  'admin_listing.created',
  'admin_listing.updated',
  'admin_listing.published',
  'admin_listing.unpublished',
  'admin_listing.tiers_changed',

  'stock.adjusted',
  'stock.received',
  'stock.reserved',
  'stock.released',
  'stock.issued',
  'stock.returned',

  'rfq.created',
  'rfq.assigned',
  'rfq.cancelled',
  'rfq.closed',

  'offer.sent',
  'offer.countered',
  'offer.accepted',
  'offer.rejected',
  'offer.withdrawn',
  'offer.expired',

  'order.created',
  'order.status_changed',
  'order.cancelled',

  'sourcing.created',
  'sourcing.invited',
  'sourcing.quoted',
  'sourcing.declined',
  'sourcing.closed',

  'purchase_order.created',
  'purchase_order.confirmed',
  'purchase_order.declined',
  'purchase_order.received',
  'purchase_order.cancelled',
  'order.allocated',
  'order.unallocated',

  'ledger.settled',

  'price_book.created',
  'price_book.updated',
  'price_book.assigned',

  'conversation.created',
  'conversation.assigned',
  'conversation.closed',

  'settings.updated',
] as const

export type AuditAction = (typeof AUDIT_ACTIONS)[number]
export const auditActionSchema = z.enum(AUDIT_ACTIONS)

export const AUDIT_ENTITY_TYPES = [
  'session',
  'org',
  'user',
  'category',
  'product',
  'product_proposal',
  'listing',
  'admin_listing',
  'stock',
  'rfq',
  'offer',
  'order',
  'sourcing_request',
  'purchase_order',
  'settlement',
  'price_book',
  'conversation',
  'settings',
] as const
export type AuditEntityType = (typeof AUDIT_ENTITY_TYPES)[number]

export const auditEventSchema = z.object({
  id: z.string(),
  seq: z.number().int(),
  action: auditActionSchema,
  entityType: z.enum(AUDIT_ENTITY_TYPES),
  entityId: z.string().nullable(),
  actorUserId: z.string().nullable(),
  actorName: z.string().nullable(),
  actorRole: roleSchema.nullable(),
  /** True when the actor was operating as the admin group rather than as themselves. */
  actingAsGroup: z.boolean(),
  summary: z.string(),
  before: z.unknown().nullable(),
  after: z.unknown().nullable(),
  requestId: z.string().nullable(),
  ip: z.string().nullable(),
  createdAt: z.number().int(),
})
export type AuditEvent = z.infer<typeof auditEventSchema>

export const auditQuerySchema = z.object({
  action: auditActionSchema.optional(),
  entityType: z.enum(AUDIT_ENTITY_TYPES).optional(),
  entityId: z.string().max(64).optional(),
  actorUserId: z.string().max(64).optional(),
  from: z.coerce.number().int().optional(),
  to: z.coerce.number().int().optional(),
})
export type AuditQuery = z.infer<typeof auditQuerySchema>

export const chainVerificationSchema = z.object({
  ok: z.boolean(),
  checked: z.number().int(),
  /** Sequence number of the first row whose hash does not match. */
  brokenAtSeq: z.number().int().nullable(),
  verifiedThroughSeq: z.number().int().nullable(),
})
export type ChainVerification = z.infer<typeof chainVerificationSchema>

/** Plain-language labels; the audit screen is read by the owner, not a developer. */
export const AUDIT_ACTION_LABELS: Record<AuditAction, string> = {
  'auth.login': 'Signed in',
  'auth.login_failed': 'Failed sign-in attempt',
  'auth.logout': 'Signed out',
  'auth.password_changed': 'Changed their password',
  'auth.session_revoked': 'Session ended',
  'org.created': 'Added a business',
  'org.updated': 'Edited a business',
  'org.status_changed': 'Changed a business status',
  'user.created': 'Created a user',
  'user.updated': 'Edited a user',
  'user.status_changed': 'Changed a user status',
  'user.password_reset': 'Reset a password',
  'category.created': 'Added a category',
  'category.updated': 'Edited a category',
  'category.deleted': 'Removed a category',
  'product.created': 'Added an item',
  'product.updated': 'Edited an item',
  'product.status_changed': 'Changed an item status',
  'product.merged': 'Merged duplicate items',
  'product_proposal.created': 'Suggested a new item',
  'product_proposal.approved': 'Approved a suggested item',
  'product_proposal.rejected': 'Rejected a suggested item',
  'product_proposal.merged': 'Matched a suggestion to an existing item',
  'listing.created': 'Added a shop item',
  'listing.updated': 'Edited a shop item',
  'listing.status_changed': 'Changed a shop item status',
  'listing.tiers_changed': 'Changed bulk prices',
  'listing.imported': 'Imported a price list',
  'admin_listing.created': 'Added a catalog item',
  'admin_listing.updated': 'Edited a catalog item',
  'admin_listing.published': 'Published to customers',
  'admin_listing.unpublished': 'Hidden from customers',
  'admin_listing.tiers_changed': 'Changed customer bulk prices',
  'stock.adjusted': 'Adjusted stock',
  'stock.received': 'Received stock',
  'stock.reserved': 'Reserved stock',
  'stock.released': 'Released reserved stock',
  'stock.issued': 'Issued stock',
  'stock.returned': 'Took stock back',
  'rfq.created': 'Asked for a price',
  'rfq.assigned': 'Assigned a price request',
  'rfq.cancelled': 'Cancelled a price request',
  'rfq.closed': 'Closed a price request',
  'offer.sent': 'Sent a quote',
  'offer.countered': 'Suggested a different price',
  'offer.accepted': 'Accepted a quote',
  'offer.rejected': 'Turned down a quote',
  'offer.withdrawn': 'Withdrew a quote',
  'offer.expired': 'A quote ran out of time',
  'order.created': 'Created an order',
  'order.status_changed': 'Moved an order along',
  'order.cancelled': 'Cancelled an order',
  'sourcing.created': 'Started a price check with shops',
  'sourcing.invited': 'Asked a shop for a price',
  'sourcing.quoted': 'A shop sent a price',
  'sourcing.declined': 'A shop passed on a price check',
  'sourcing.closed': 'Finished a price check',
  'purchase_order.created': 'Ordered from a shop',
  'purchase_order.confirmed': 'A shop accepted an order',
  'purchase_order.declined': 'A shop turned an order down',
  'purchase_order.received': 'Goods came in from a shop',
  'purchase_order.cancelled': 'Cancelled an order to a shop',
  'order.allocated': 'Chose where an order is supplied from',
  'order.unallocated': 'Removed a supply source',
  'ledger.settled': 'Recorded a payment',
  'price_book.created': 'Created a price arrangement',
  'price_book.updated': 'Changed a price arrangement',
  'price_book.assigned': 'Changed who is on a price arrangement',
  'conversation.created': 'Started a conversation',
  'conversation.assigned': 'Assigned a conversation',
  'conversation.closed': 'Closed a conversation',
  'settings.updated': 'Changed settings',
}
